import type { Event, Exception, SeverityLevel, StackFrame } from '@sentry/react';
import { isChunkLoadError } from 'c-lib';

/**
 * Error families Sentry groups badly on its own. Agreed with Ben (Oct 2026): keep every one of them,
 * but as one issue per family; third-party ones at warning level.
 *
 * - Third-party code: errors thrown by scripts that aren't ours, such as Google Maps internals
 *   (`can't access property "dM", p is undefined`), browser extensions (`NS_ERROR_FAILURE` from
 *   `/modules.<hash>.js`) and scripts injected by crawlers. Minified names and hashed filenames
 *   change with each release, so each version opened a new issue.
 * - Outlook's link scanner, which rejects with a string (`Object Not Found Matching Id:4, ...`),
 *   so each id opened its own issue.
 * - Chunk-load failures after a deploy: the hashed chunk URL is in the message.
 * - react-dnd's "two HTML5 backends", whose stacks differ per page.
 */
export type ErrorFamily = {
    fingerprint: string[];
    level?: SeverityLevel;
    tags: Record<string, string>;
    contexts?: Record<string, Record<string, unknown>>;
    /** Replaces an empty exception value, so the title names the culprit. */
    value?: string;
};

const OUTLOOK_SCANNER = /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/;
const NO_MESSAGE = /^(|No error message)$/;
const HASH = /[.-][0-9a-f]{8,}(?=\.js$)/i;
// Vite serves our bundle from /assets/ in a build and /src/, /node_modules/, /@vite/... in dev.
const OWN_PATH = /^\/(assets|src|node_modules|@[\w-]+)\//;

const parseUrl = (filename: string) => {
    try {
        return new URL(filename);
    } catch {
        return null;
    }
};

/**
 * Who a stack frame's script belongs to: `null` when it is ours, otherwise a stable name for the
 * script (no version hash), e.g. `maps.googleapis.com/maps-api-v3`, `modules.js`, `inline script`.
 */
export const scriptOwner = (filename: string | undefined, appOrigin: string): string | null => {
    if (!filename || filename === '<anonymous>') return 'inline script';
    const url = parseUrl(filename);
    if (url == null) return OWN_PATH.test(filename) ? null : filename.replace(HASH, '');
    if (url.protocol.endsWith('-extension:')) return url.protocol.replace(/:$/, '');
    if (url.origin === appOrigin && OWN_PATH.test(url.pathname)) return null;

    const [first = ''] = url.pathname.split('/').filter(Boolean);
    const firstSegment = first.replace(HASH, '');
    return url.origin === appOrigin ? firstSegment : `${url.host}/${firstSegment}`;
};

/** The frame the error was thrown from (Sentry orders frames oldest first), skipping native ones. */
const throwingFrame = (exception: Exception): StackFrame | undefined =>
    [...(exception.stacktrace?.frames ?? [])].reverse().find(frame => frame.lineno != null);

export function errorFamilyOf(event: Event, appOrigin: string): ErrorFamily | null {
    const exception = event.exception?.values?.at(-1);
    if (exception == null) return null;
    const value = exception.value ?? '';

    if (
        OUTLOOK_SCANNER.test(
            value.replace(/^Non-Error promise rejection captured with value: /, ''),
        )
    ) {
        return {
            fingerprint: ['third-party', 'outlook-link-scanner'],
            level: 'warning',
            tags: { 'error.source': 'third-party', 'third_party.script': 'outlook-link-scanner' },
        };
    }

    if (isChunkLoadError(value)) {
        return {
            fingerprint: ['chunk-load-failed'],
            tags: { 'error.source': 'deploy' },
            contexts: { chunk: { url: value.match(/https?:\/\/\S+/)?.[0] ?? null } },
        };
    }

    if (value === 'Cannot have two HTML5 backends at the same time.') {
        return { fingerprint: ['react-dnd', 'two-html5-backends'], tags: {} };
    }

    const frame = throwingFrame(exception);
    const owner = frame == null ? null : scriptOwner(frame.filename, appOrigin);
    if (owner == null) return null;

    return {
        fingerprint: ['third-party', owner, exception.type ?? 'Error'],
        level: 'warning',
        tags: { 'error.source': 'third-party', 'third_party.script': owner },
        ...(NO_MESSAGE.test(value) ? { value: `No error message (thrown in ${owner})` } : {}),
    };
}
